<template>
	<v-card class="flex round-card background-2 w100p dms-component" elevation="1">
		<v-card-title class="pa-3">
			<p class="pl-2 pl-xl-3">Drive</p>
			<v-spacer></v-spacer>
			<v-spacer></v-spacer>
			<v-row class="pa-0 ma-0 pr-4">
				<v-text-field class="hidden-xs-only font-weight-regular"
					append-icon="mdi-magnify"
					label="Cerca"
					v-model="search"
					clearable
					hide-details outlined dense
				></v-text-field>
			</v-row>
			<v-tooltip top>
				<template v-slot:activator="{ on }">
					<v-btn @click="showHelp = !showHelp" v-on="on" icon>
						<v-icon color="info">mdi-information-outline</v-icon>
					</v-btn>
				</template>
				<span>Come funziona questa sezione</span>
			</v-tooltip>
			<v-tooltip top>
				<template v-slot:activator="{ on }">
					<v-btn @click="loadContent()" :loading="loading" v-on="on" icon>
						<v-icon>mdi-refresh</v-icon>
					</v-btn>
				</template>
				<span>Aggiorna</span>
			</v-tooltip>
		</v-card-title>
		<v-divider></v-divider>

		<!-- Info box -->
		<v-alert
			v-model="showHelp"
			dismissible
			dense
			text
			type="info"
			icon="mdi-information-outline"
			class="ma-3 mb-0 body-2"
			@input="onHelpDismissed">
			<div class="font-weight-medium mb-1">Archivio della documentazione aziendale</div>
			<div>
				Qui trovi i documenti condivisi dell'azienda: manuali, procedure, modulistica, listini.
				Sfoglia le cartelle, fai doppio clic su un file per aprirne l'anteprima, e usa la ricerca
				con l'opzione <strong>Ovunque</strong> per cercare in tutto l'archivio. Puoi scaricare un
				singolo file, un'intera cartella come ZIP, oppure selezionare più elementi e scaricarli
				insieme.
			</div>
			<div v-if="canCreate || canEdit" class="mt-2">
				Per pubblicare: trascina i file nell'area sottostante o usa <strong>Carica</strong>, anche
				per un'intera cartella. Su un documento già presente puoi caricare una nuova versione senza
				perdere la precedente.
			</div>
			<div v-if="canEdit" class="mt-2">
				Con il tasto destro su una cartella › <strong>Permessi cartella</strong> scegli quali ruoli
				possono consultarla. Se non selezioni nessun ruolo la cartella non è riservata: eredita dalla
				cartella superiore, o è visibile a tutti se nessuna cartella superiore ha restrizioni. Chi ha
				i permessi di modifica vede sempre l'intero archivio.
			</div>
			<div v-else class="mt-2">
				L'icona <v-icon x-small color="blue-grey">mdi-folder-lock</v-icon> segnala una cartella con
				accesso limitato per ruolo: se non ne vedi il contenuto è riservata ad altri ruoli.
			</div>
		</v-alert>
		<v-container fluid class="pa-4">
		<!-- Toolbar -->
		<v-row no-gutters class="mb-3 align-center">
			<v-col cols="12" md="6">
				<v-breadcrumbs :items="breadcrumbItems" class="pa-0">
					<template v-slot:item="{ item }">
						<v-breadcrumbs-item
							:disabled="item.disabled"
							@click="navigateToFolder(item.folderId)"
							class="cursor-pointer"
						>
							{{ item.text }}
						</v-breadcrumbs-item>
					</template>
				</v-breadcrumbs>
			</v-col>
			<v-col cols="12" md="6" class="d-flex justify-end align-center">
				<v-btn-toggle v-model="viewMode" mandatory dense class="mr-2">
					<v-btn small value="grid"><v-icon small>mdi-view-grid</v-icon></v-btn>
					<v-btn small value="list"><v-icon small>mdi-view-list</v-icon></v-btn>
				</v-btn-toggle>
				<v-btn v-if="canCreate" color="primary" small @click="newFolderDialog = true" class="mr-2">
					<v-icon left small class="d-none d-sm-inline">mdi-folder-plus</v-icon>
					Nuova Cartella
				</v-btn>
				<v-btn v-if="canCreate" color="success" small class="upload-btn">
					<v-icon left small class="d-none d-sm-inline">mdi-upload</v-icon>
					Carica
					<input
						ref="fileInput"
						type="file"
						multiple
						class="upload-btn-input"
						@change="handleFileUpload"
					/>
				</v-btn>
				<v-btn v-if="canCreate" color="success" small outlined class="upload-btn ml-2">
					<v-icon left small class="d-none d-sm-inline">mdi-folder-upload</v-icon>
					Carica cartella
					<input
						ref="folderInput"
						type="file"
						webkitdirectory
						multiple
						class="upload-btn-input"
						@change="handleFolderUpload"
					/>
				</v-btn>
			</v-col>
		</v-row>

		<!-- Storage info -->
		<v-row no-gutters class="mb-2 align-center" style="min-height: 40px">
			<v-spacer></v-spacer>
			<v-col cols="auto" class="text-caption grey--text">
				<v-icon x-small class="mr-1">mdi-harddisk</v-icon>{{ formatSize(storage.total) }}
			</v-col>
		</v-row>

		<!-- Search scope toggle -->
		<v-row v-if="search" no-gutters class="mb-2 align-center">
			<v-chip-group v-model="searchScope" mandatory>
				<v-chip small filter value="local">Cartella corrente</v-chip>
				<v-chip small filter value="global">Ovunque</v-chip>
			</v-chip-group>
		</v-row>

		<!-- Bulk toolbar -->
		<v-card v-if="hasSelection" outlined class="mb-3 bulk-toolbar">
			<v-card-text class="d-flex align-center py-2">
				<v-icon class="mr-2" color="primary">mdi-checkbox-multiple-marked</v-icon>
				<span class="text-body-2">{{ selectionCount }} elemento/i selezionato/i</span>
				<v-spacer></v-spacer>
				<v-btn v-if="canEdit" small text @click="openBulkMoveDialog"><v-icon left small>mdi-folder-move</v-icon>Sposta</v-btn>
				<v-btn small text @click="bulkDownload" :loading="bulkLoading"><v-icon left small>mdi-download</v-icon>Scarica ZIP</v-btn>
				<v-btn v-if="canShare && selectedDocIds.length" small text color="info" @click="openBulkShareDialog"><v-icon left small>mdi-share-variant</v-icon>Condividi</v-btn>
				<v-btn v-if="canDelete" small text color="error" @click="bulkDeleteDialog = true"><v-icon left small>mdi-delete</v-icon>Elimina</v-btn>
				<v-btn small icon @click="clearSelection"><v-icon>mdi-close</v-icon></v-btn>
			</v-card-text>
		</v-card>

		<!-- Drop zone -->
		<div
			ref="dropZone"
			class="drop-zone"
			:class="{ 'drop-zone-active': isDragging, 'marquee-active': marquee.active }"
			style="max-height: 500px; overflow-y: auto; position: relative"
			@dragenter.prevent="onDragEnter"
			@dragover.prevent
			@dragleave.prevent="onDragLeave"
			@drop.prevent="handleDrop"
			@mousedown.left="onMarqueeMouseDown"
		>
			<!-- Marquee overlay -->
			<div v-if="marquee.active" class="marquee-rect" :style="{
				left: marquee.x + 'px',
				top: marquee.y + 'px',
				width: marquee.w + 'px',
				height: marquee.h + 'px'
			}"></div>
			<!-- Upload progress -->
			<v-progress-linear v-if="uploading" :value="uploadProgress" color="primary" class="mb-3" height="6" rounded></v-progress-linear>

			<!-- Cartelle -->
			<div v-if="filteredFolders.length && !isGlobalSearch" class="mb-4">
				<div class="text-overline grey--text mb-2">Cartelle</div>
				<!-- Vista griglia -->
				<v-row v-if="viewMode === 'grid'" dense>
					<v-col v-for="folder in filteredFolders" :key="'f-' + folder.id" cols="6" sm="4" md="3" lg="2">
						<v-card
							outlined
							class="text-center pa-3 cursor-pointer hover-card d-flex flex-column align-center justify-center"
							style="height: 140px"
							:class="{ 'dragging': dragItem && dragItem.type === 'folder' && dragItem.id === folder.id, 'drag-over': dragOverFolder === folder.id, 'selected-multi': selectedFolderIds.includes(folder.id) }"
							:data-folder-id="folder.id"
							draggable="true"
							@click="navigateToFolder(folder.id)"
							@contextmenu.prevent="showFolderMenu($event, folder)"
							@dragstart="onItemDragStart($event, 'folder', folder)"
							@dragend="onItemDragEnd"
							@dragover.prevent.stop="onFolderDragOver($event, folder)"
							@dragleave.prevent.stop="onFolderDragLeave"
							@drop.prevent.stop="onFolderDrop($event, folder)"
						>
							<v-icon size="48" :color="folder.acl_restricted ? 'blue-grey' : 'amber darken-2'">
								{{ folder.acl_restricted ? 'mdi-folder-lock' : 'mdi-folder' }}
							</v-icon>
							<div class="text-body-2 mt-1 text-truncate">{{ folder.name }}</div>
							<div class="text-caption grey--text">
								<span v-if="folder.acl_readable === false">Solo accesso</span>
								<span v-else>{{ folder.documents_count || 0 }} file<span v-if="folder.documents_sum_size"> · {{ formatSize(folder.documents_sum_size) }}</span></span>
							</div>
						</v-card>
					</v-col>
				</v-row>
				<!-- Vista lista -->
				<v-simple-table v-else dense class="bordered-table">
					<tbody>
						<tr
							v-for="folder in filteredFolders"
							:key="'f-' + folder.id"
							class="cursor-pointer"
							:class="{ 'dragging': dragItem && dragItem.type === 'folder' && dragItem.id === folder.id, 'drag-over': dragOverFolder === folder.id }"
							draggable="true"
							@click="navigateToFolder(folder.id)"
							@contextmenu.prevent="showFolderMenu($event, folder)"
							@dragstart="onItemDragStart($event, 'folder', folder)"
							@dragend="onItemDragEnd"
							@dragover.prevent.stop="onFolderDragOver($event, folder)"
							@dragleave.prevent.stop="onFolderDragLeave"
							@drop.prevent.stop="onFolderDrop($event, folder)"
						>
							<td style="width: 40px">
								<v-icon :color="folder.acl_restricted ? 'blue-grey' : 'amber darken-2'">
									{{ folder.acl_restricted ? 'mdi-folder-lock' : 'mdi-folder' }}
								</v-icon>
							</td>
							<td>{{ folder.name }}</td>
							<td class="text-caption grey--text">
								<!-- acl_restricted dice che la cartella HA una restrizione,
								     non che sia preclusa a chi la sta guardando: usarlo qui
								     etichettava "Accesso riservato" anche a chi vi accede -->
								<span v-if="folder.acl_readable === false">Contenuto riservato</span>
								<span v-else-if="folder.acl_restricted">Accesso limitato per ruolo</span>
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</div>

			<!-- Documenti -->
			<div v-if="filteredDocuments.length">
				<div class="text-overline grey--text mb-2">Documenti</div>
				<!-- Vista griglia -->
				<v-row v-if="viewMode === 'grid'" dense>
					<v-col v-for="doc in filteredDocuments" :key="'d-' + doc.id" cols="6" sm="4" md="3" lg="2">
						<v-card
							outlined
							class="dms-doc-card"
							:class="{ 'dragging': dragItem && dragItem.type === 'doc' && dragItem.id === doc.id, 'selected': selectedDoc && selectedDoc.id === doc.id, 'selected-multi': selectedDocIds.includes(doc.id) }"
							:data-doc-id="doc.id"
							draggable="true"
							@dragstart="onItemDragStart($event, 'doc', doc)"
							@dragend="onItemDragEnd"
							@click="toggleSelectDocument(doc)"
							@dblclick.stop="isPreviewable(doc) ? openPreview(doc) : downloadDoc(doc)"
							@contextmenu.prevent="showDocMenu($event, doc)"
						>
							<div class="dms-doc-preview">
								<img v-if="doc.thumbnail" :src="'data:image/png;base64,' + doc.thumbnail" />
								<v-icon v-else size="64" :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
								<div class="dms-doc-tags" v-if="doc.tags && doc.tags.length">
									<v-chip v-for="tag in doc.tags.slice(0, 2)" :key="tag.id" x-small :color="tag.color || 'grey'" text-color="white">{{ tag.name }}</v-chip>
									<v-chip v-if="doc.tags.length > 2" x-small color="grey" text-color="white">+{{ doc.tags.length - 2 }}</v-chip>
								</div>
								<v-chip v-if="canShare && doc.shares_count > 0" x-small color="info" text-color="white" class="dms-doc-shared">
									<v-icon left x-small>mdi-share-variant</v-icon>{{ doc.shares_count }}
								</v-chip>
							</div>
							<div class="dms-doc-info">
								<v-icon size="18" :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
								<span class="text-body-2 text-truncate" style="flex: 1; min-width: 0">{{ doc.name | fileName }}.{{ doc.ext }}</span>
								<v-btn icon x-small @click.stop="showDocMenu($event, doc)"><v-icon>mdi-dots-vertical</v-icon></v-btn>
							</div>
						</v-card>
					</v-col>
				</v-row>
				<!-- Vista lista -->
				<v-simple-table v-else dense class="bordered-table">
					<thead>
						<tr>
							<th style="width: 40px"></th>
							<th>Nome</th>
							<th v-if="isGlobalSearch" class="d-none d-sm-table-cell">Cartella</th>
							<th class="d-none d-sm-table-cell">Dimensione</th>
							<th class="d-none d-sm-table-cell">Data</th>
							<th class="d-none d-md-table-cell">Caricato da</th>
							<th class="d-none d-md-table-cell">Tag</th>
							<th style="width: 60px"></th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="doc in filteredDocuments"
							:key="'d-' + doc.id"
							class="cursor-pointer"
							:class="{ 'dragging': dragItem && dragItem.type === 'doc' && dragItem.id === doc.id }"
							draggable="true"
							@click="debouncedOpenDocDetails(doc)"
							@dblclick.stop="cancelDebouncedClick(); isPreviewable(doc) ? openPreview(doc) : downloadDoc(doc)"
							@contextmenu.prevent="showDocMenu($event, doc)"
							@dragstart="onItemDragStart($event, 'doc', doc)"
							@dragend="onItemDragEnd"
						>
							<td><v-icon :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon></td>
							<td>{{ doc.name | fileName(doc.ext) }}</td>
							<td v-if="isGlobalSearch" class="d-none d-sm-table-cell text-no-wrap text-caption">
								<v-icon x-small class="mr-1">mdi-folder</v-icon>{{ doc.folder ? doc.folder.name : 'Drive' }}
							</td>
							<td class="d-none d-sm-table-cell text-no-wrap">{{ formatSize(doc.size) }}</td>
							<td class="d-none d-sm-table-cell text-no-wrap">{{ doc.created_at | date }}</td>
							<td class="d-none d-md-table-cell text-no-wrap">{{ doc.uploaded_by ? doc.uploaded_by.name : '-' }}</td>
							<td class="d-none d-md-table-cell">
								<v-chip v-for="tag in (doc.tags || []).slice(0, 3)" :key="tag.id" x-small :color="tag.color || 'grey'" text-color="white" class="mr-1">
									{{ tag.name }}
								</v-chip>
								<span v-if="(doc.tags || []).length > 3" class="text-caption grey--text">+{{ doc.tags.length - 3 }}</span>
							</td>
							<td>
								<v-btn icon x-small @click.stop="downloadDoc(doc)"><v-icon small>mdi-download</v-icon></v-btn>
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</div>

			<!-- Empty state -->
			<div v-if="!loading && !filteredFolders.length && !filteredDocuments.length" class="empty-state text-center">
				<!-- Cartella navigabile ma con contenuto riservato: dirle "vuota"
				     manderebbe l'utente a cercare un problema che non esiste -->
				<template v-if="!currentFolderReadable">
					<v-icon size="64" color="blue-grey lighten-2">mdi-folder-lock</v-icon>
					<div class="text-h6 grey--text mt-3">Contenuto riservato</div>
					<div class="text-body-2 grey--text">
						Il contenuto di questa cartella è riservato ad altri ruoli.
						Le sottocartelle a cui hai accesso restano visibili qui sopra.
					</div>
				</template>
				<template v-else>
					<v-icon size="64" color="grey lighten-1">mdi-folder-text-outline</v-icon>
					<div class="text-h6 grey--text mt-3">Nessun documento</div>
					<div class="text-body-2 grey--text" v-if="canCreate">Trascina i file qui o usa il pulsante Carica per pubblicare la documentazione</div>
					<div class="text-body-2 grey--text" v-else>In questa cartella non è ancora presente documentazione</div>
				</template>
			</div>

			<v-progress-circular v-if="loading" indeterminate color="primary" class="d-block mx-auto my-12"></v-progress-circular>

			<!-- Drag overlay -->
			<div v-if="isDragging && canCreate" class="drop-overlay">
				<v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
				<div class="text-h6 primary--text mt-2">Rilascia i file qui</div>
			</div>
		</div>

		<!-- Context menu -->
		<v-menu v-model="contextMenu.show" :position-x="contextMenu.x" :position-y="contextMenu.y" absolute content-class="round-card">
			<v-list dense>
				<v-list-item @click="openPreview(contextMenu.item)" v-if="contextMenu.type === 'doc' && isPreviewable(contextMenu.item)">
					<v-list-item-icon><v-icon small>mdi-eye</v-icon></v-list-item-icon>
					<v-list-item-title>Anteprima</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openDocDetails(contextMenu.item)" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-information-outline</v-icon></v-list-item-icon>
					<v-list-item-title>Dettagli</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadDoc(contextMenu.item)" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-download</v-icon></v-list-item-icon>
					<v-list-item-title>Scarica</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadFolder(contextMenu.item)" v-if="contextMenu.type === 'folder'">
					<v-list-item-icon><v-icon small>mdi-folder-download</v-icon></v-list-item-icon>
					<v-list-item-title>Scarica cartella</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openShareDialog()" v-if="contextMenu.type === 'doc' && canShare">
					<v-list-item-icon><v-icon small>mdi-share-variant</v-icon></v-list-item-icon>
					<v-list-item-title>Condividi con azienda</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openFolderRolesDialog()" v-if="contextMenu.type === 'folder' && canEdit">
					<v-list-item-icon><v-icon small>mdi-shield-account-outline</v-icon></v-list-item-icon>
					<v-list-item-title>Permessi cartella</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openRenameDialog()" v-if="canEdit">
					<v-list-item-icon><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
					<v-list-item-title>Rinomina</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openMoveDialog()" v-if="canEdit">
					<v-list-item-icon><v-icon small>mdi-folder-move</v-icon></v-list-item-icon>
					<v-list-item-title>Sposta</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openTagDialog()" v-if="contextMenu.type === 'doc' && canEdit">
					<v-list-item-icon><v-icon small>mdi-tag</v-icon></v-list-item-icon>
					<v-list-item-title>Tag</v-list-item-title>
				</v-list-item>
				<v-list-item @click="confirmDelete()" v-if="canDelete">
					<v-list-item-icon><v-icon small color="red">mdi-delete</v-icon></v-list-item-icon>
					<v-list-item-title class="red--text">Elimina</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>

		<!-- Dettagli documento (sidebar) -->
		<v-navigation-drawer v-model="detailDrawer" right app clipped temporary width="380">
			<template v-if="selectedDoc">
				<v-card flat class="background-1">
					<v-card-title class="d-flex align-center" style="flex-wrap: nowrap">
						<v-icon left :color="getFileColor(selectedDoc.ext)" class="flex-shrink-0">{{ getFileIcon(selectedDoc.ext) }}</v-icon>
						<span class="text-subtitle-1 text-truncate" style="min-width: 0; flex: 1">{{ selectedDoc.name | fileName(selectedDoc.ext) }}</span>
						<v-btn icon small class="flex-shrink-0 ml-auto" @click="detailDrawer = false"><v-icon>mdi-close</v-icon></v-btn>
					</v-card-title>
					<v-divider></v-divider>
					<v-card-text>
						<div class="mb-3">
							<div class="text-caption grey--text">Nome file</div>
							<div>{{ selectedDoc.name | fileName(selectedDoc.ext) }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Dimensione</div>
							<div>{{ formatSize(selectedDoc.size) }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Data caricamento</div>
							<div>{{ selectedDoc.created_at | date }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Caricato da</div>
							<div>{{ selectedDoc.uploaded_by ? selectedDoc.uploaded_by.name : '-' }}</div>
						</div>
						<div class="mb-3" v-if="selectedDoc.description">
							<div class="text-caption grey--text">Descrizione</div>
							<div>{{ selectedDoc.description }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Versione</div>
							<div>{{ selectedDoc.version }}</div>
						</div>
						<div class="mb-3" v-if="selectedDoc.tags && selectedDoc.tags.length">
							<div class="text-caption grey--text">Tag</div>
							<v-chip v-for="tag in selectedDoc.tags" :key="tag.id" small :color="tag.color || 'grey'" text-color="white" class="mr-1 mt-1">
								{{ tag.name }}
							</v-chip>
						</div>

						<!-- Condivisioni con le aziende -->
						<div class="mb-3" v-if="canShare">
							<div class="text-caption grey--text mb-1">Condiviso con</div>
							<template v-if="docShares.length">
								<v-chip
									v-for="share in docShares"
									:key="share.id"
									small
									class="mr-1 mt-1"
									close
									@click:close="removeShare(share)"
								>
									{{ share.company ? (share.company.business_name || (share.company.name + ' ' + share.company.surname)) : '-' }}
								</v-chip>
							</template>
							<div v-else class="text-body-2 grey--text">Nessuna azienda</div>
						</div>

						<!-- Versioni -->
						<div class="mb-3" v-if="docVersions.length">
							<div class="text-caption grey--text mb-1">Versioni precedenti</div>
							<v-list dense class="pa-0">
								<v-list-item v-for="ver in docVersions" :key="ver.id" dense>
									<v-list-item-content>
										<v-list-item-title class="text-body-2">v{{ ver.version }} - {{ ver.original_name }}</v-list-item-title>
										<v-list-item-subtitle>{{ ver.created_at | date }} - {{ formatSize(ver.size) }}</v-list-item-subtitle>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</div>
					</v-card-text>
					<v-card-actions>
						<v-btn color="primary" text @click="downloadDoc(selectedDoc)">
							<v-icon left small>mdi-download</v-icon>Scarica
						</v-btn>
						<v-btn v-if="canShare" color="info" text @click="openShareDialog()">
							<v-icon left small>mdi-share-variant</v-icon>Condividi
						</v-btn>
						<template v-if="canCreate">
							<v-btn color="success" text @click="$refs.versionInput.click()">
								<v-icon left small>mdi-upload</v-icon>Nuova Versione
							</v-btn>
							<input
								ref="versionInput"
								type="file"
								style="position: absolute; width: 0; height: 0; overflow: hidden; opacity: 0"
								@change="handleVersionUpload"
							/>
						</template>
					</v-card-actions>
				</v-card>
			</template>
		</v-navigation-drawer>

		<DmsRecentActivity ref="recentActivity" :api-base="apiBase" />

		<!-- Dialog nuova cartella -->
		<v-dialog v-model="newFolderDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Nuova Cartella</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-text-field v-model="newFolderName" label="Nome cartella" outlined dense autofocus @keyup.enter="createFolder"></v-text-field>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="newFolderDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="createFolder" :loading="saving">Crea</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog rinomina -->
		<v-dialog v-model="renameDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Rinomina</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-text-field v-model="renameName" label="Nuovo nome" outlined dense autofocus @keyup.enter="renameItem" :rules="[v => !/[\\\\/:*?&quot;<>|]/.test(v) || 'Caratteri non consentiti: \\\\ / : * ? &quot; < > |']"></v-text-field>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="renameDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="renameItem" :loading="saving">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog tag -->
		<v-dialog v-model="tagDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Gestisci Tag</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-combobox
						ref="tagCombobox"
						v-model="selectedTags"
						:items="allTags"
						item-text="name"
						item-value="id"
						label="Tag"
						multiple
						chips
						small-chips
						deletable-chips
						outlined
						dense
					></v-combobox>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="tagDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="saveTags" :loading="saving">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog sposta -->
		<v-dialog v-model="moveDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Sposta in...</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-list dense class="pa-0">
						<v-list-item @click="moveFolderTarget = null" :class="{ 'v-list-item--active primary--text': moveFolderTarget === null }">
							<v-list-item-icon class="mr-2 pr-2"><v-icon color="amber darken-2">mdi-folder-home</v-icon></v-list-item-icon>
							<v-list-item-title>Drive</v-list-item-title>
						</v-list-item>
					</v-list>
					<v-treeview
						:items="folderTree"
						item-key="id"
						item-text="name"
						activatable
						@update:active="moveFolderTarget = $event[0] || null"
						open-on-click
						dense
					>
						<template v-slot:prepend="{}">
							<v-icon color="amber darken-2">mdi-folder</v-icon>
						</template>
					</v-treeview>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="moveDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="moveItem" :loading="saving">Sposta</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog permessi cartella -->
		<v-dialog v-model="folderRolesDialog" max-width="520" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Permessi cartella</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-progress-circular v-if="folderRolesLoading" indeterminate color="primary" class="d-block mx-auto my-6"></v-progress-circular>
					<template v-else>
						<p class="mb-3">
							Ruoli abilitati a consultare e scaricare il contenuto di
							<strong>{{ folderRolesTarget ? folderRolesTarget.name : '' }}</strong>.
						</p>

						<v-alert v-if="folderRolesInherited" type="info" dense text class="text-caption">
							Questa cartella eredita i permessi da
							<strong>{{ folderRolesInheritedFrom ? folderRolesInheritedFrom.name : 'una cartella superiore' }}</strong>.
							Selezionando dei ruoli qui imposti una configurazione propria che sostituisce quella ereditata.
						</v-alert>

						<v-select
							v-model="folderRoleIds"
							:items="availableRoles"
							item-text="name"
							item-value="id"
							label="Ruoli abilitati"
							:menu-props="{'offset-y': true}"
							multiple chips small-chips deletable-chips
							outlined dense
						></v-select>

						<p class="text-caption grey--text mb-0">
							Lasciando la selezione vuota non imposti nessuna restrizione: la cartella eredita
							dalla cartella superiore, oppure è visibile a tutti se nessuna cartella superiore
							ha restrizioni. Chi ha i permessi di modifica sul Drive vede sempre
							tutte le cartelle.
						</p>
					</template>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="folderRolesDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="saveFolderRoles" :loading="saving" :disabled="folderRolesLoading">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog conferma eliminazione -->
		<v-dialog v-model="deleteDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Conferma Eliminazione</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					Sei sicuro di voler eliminare <strong>{{ contextMenu.item ? contextMenu.item.name : '' }}</strong>?
					<span v-if="contextMenu.type === 'folder'">Tutti i contenuti della cartella verranno eliminati.</span>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="deleteDialog = false">Annulla</v-btn>
					<v-btn text color="error" @click="deleteItem" :loading="saving">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: bulk move -->
		<v-dialog v-model="bulkMoveDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Sposta {{ selectionCount }} elemento/i</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<p class="mb-2">Scegli la cartella di destinazione.</p>
					<v-select
						v-model="bulkMoveFolderTarget"
						:items="[{ id: null, name: '— Radice —' }].concat(folders)"
						item-text="name"
						item-value="id"
						label="Cartella destinazione"
						:menu-props="{'offset-y': true}"
						outlined dense clearable
					></v-select>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkMoveDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="confirmBulkMove" :loading="bulkLoading">Sposta</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: bulk delete -->
		<v-dialog v-model="bulkDeleteDialog" max-width="450" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Conferma eliminazione</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					Eliminare <strong>{{ selectedDocIds.length }}</strong> documento/i e <strong>{{ selectedFolderIds.length }}</strong> cartella/e?
					<span v-if="selectedFolderIds.length">
						<br><br><v-icon small color="warning">mdi-alert</v-icon>
						Le cartelle saranno eliminate con tutto il loro contenuto.
					</span>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkDeleteDialog = false">Annulla</v-btn>
					<v-btn text color="error" @click="confirmBulkDelete" :loading="bulkLoading">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: condivisione singola -->
		<v-dialog v-model="shareDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Condividi documento</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<div class="body-2 mb-3">
						Il documento comparira' nell'area privata delle aziende selezionate.
					</div>
					<async-autocomplete
						v-model="shareCompanyIds"
						async-url="async-companies"
						label="Aziende"
						multiple
						chips
						small-chips
						deletable-chips
						clearable
						:return-object="true"
					></async-autocomplete>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="shareDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="shareDocument" :loading="saving" :disabled="!shareCompanyIds.length">Condividi</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: condivisione multipla -->
		<v-dialog v-model="bulkShareDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Condividi documenti</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<div class="body-2 mb-3">
						<strong>{{ selectedDocIds.length }}</strong> documento/i comparira'/nno nell'area privata delle aziende selezionate.
					</div>
					<async-autocomplete
						v-model="bulkShareCompanyIds"
						async-url="async-companies"
						label="Aziende"
						multiple
						chips
						small-chips
						deletable-chips
						clearable
						:return-object="true"
					></async-autocomplete>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkShareDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="confirmBulkShare" :loading="bulkLoading" :disabled="!bulkShareCompanyIds.length">Condividi</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<file-preview-dialog
			v-model="previewDialog"
			:doc="previewDoc"
			:url="previewUrl"
		/>

		<v-snackbar v-model="snackbar.value" :color="snackbar.color" :timeout="snackbar.timeout || 4000">
			{{ snackbar.text }}
			<template v-slot:action="{ attrs }">
				<v-btn dark icon v-bind="attrs" @click="snackbar.value = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
	</v-container>
	</v-card>
</template>

<script>
import DmsMixin from '@/mixins/DmsMixin'
import DmsRecentActivity from '@/components/common/DmsRecentActivity'
import FilePreviewDialog from '@/components/common/FilePreviewDialog'
import AsyncAutocomplete from '@/components/common/AsyncAutocomplete'

export default {
	name: 'Drive',
	mixins: [DmsMixin],
	components: { DmsRecentActivity, FilePreviewDialog, AsyncAutocomplete },

	data() {
		return {
			showHelp: true,
			// Condivisione verso le aziende, ex File Condivisi.
			docShares: [],
			shareDialog: false,
			shareCompanyIds: [],
			bulkShareDialog: false,
			bulkShareCompanyIds: [],
			// Il backend dice se la cartella in cui siamo e' leggibile: senza,
			// una cartella riservata e una vuota sono indistinguibili.
			currentFolderReadable: true,
			folderRolesDialog: false,
			folderRolesLoading: false,
			folderRolesTarget: null,
			folderRoleIds: [],
			folderRolesInherited: false,
			folderRolesInheritedFrom: null,
			availableRoles: []
		}
	},

	// router.beforeEach accende il progress globale a ogni navigazione e sta al
	// componente di destinazione spegnerlo. AppTabs tiene la router-view dietro
	// un v-show="!progress": senza questa riga la pagina resta sullo spinner.
	// (Il created() di DmsMixin viene eseguito comunque: i mixin girano prima.)
	created() {
		this.$store.commit('endProgress')
		this.showHelp = localStorage.getItem('drive-help-dismissed') !== '1'
	},

	computed: {
		// I permessi arrivano da role.permissions, popolato da user-details.
		// Il terzo argomento si omette: il default booleano true fa gia' il
		// match su 'true' e restituisce false su 'false'.
		canCreate() {
			return this.$store.getters.checkRolePermissions('drive', 'create')
		},

		canEdit() {
			return this.$store.getters.checkRolePermissions('drive', 'edit')
		},

		canDelete() {
			return this.$store.getters.checkRolePermissions('drive', 'delete')
		},

		/**
		 * La condivisione pubblica un documento sul portale di un'azienda:
		 * senza il modulo Portal non c'e' nessuno che possa vederlo, e infatti
		 * il backend non registra nemmeno le rotte. Serve anche il permesso di
		 * modifica sul Drive, perche' condividere e' una scrittura.
		 */
		canShare() {
			return this.canEdit && this.$store.getters.checkModulePermissions('portal')
		}
	},

	methods: {
		dmsConfig() {
			return {
				apiBase: 'drive',
				breadcrumbRootLabel: 'Drive',
				viewModeStorageKey: 'drive-view-mode',
				hasRecentActivity: true,
				defaultViewMode: 'list'
			}
		},

		/**
		 * Input:  nessuno (usa currentFolderId)
		 * Fa:     come loadContent del mixin, ma conserva anche
		 *         current_folder_readable — il mixin scarta i campi che non
		 *         conosce, e senza quel flag una cartella riservata e una vuota
		 *         sono indistinguibili nella UI
		 * Output: nessuno
		 */
		async loadContent() {
			this.loading = true
			try {
				var resp = await this.axios.get(this.apiBase + '/folders', {
					params: { parent_id: this.currentFolderId }
				})
				if (resp.data.success) {
					this.folders = resp.data.data.folders || []
					this.documents = resp.data.data.documents || []
					this.breadcrumb = resp.data.data.breadcrumb || []
					this.storage = resp.data.data.storage || { total: 0 }
					this.currentFolderReadable = resp.data.data.current_folder_readable !== false
				}
			} catch (e) {
				// silent, come nel mixin
			} finally {
				this.loading = false
			}
		},

		onHelpDismissed(value) {
			if (!value) localStorage.setItem('drive-help-dismissed', '1')
		},

		// --- Guardie di scrittura ---
		// Nascondere i pulsanti non basta: drag&drop e rilascio di file sulla
		// drop zone passano dai metodi del mixin senza toccare la UI. Il backend
		// risponderebbe comunque 403, ma l'utente vedrebbe una barra di
		// avanzamento e poi un errore per ogni file. Meglio fermarsi qui.
		//
		// La guardia sta su TUTTI i metodi che scrivono, non solo su quelli
		// raggiungibili oggi: i dialog di rinomina, spostamento, tag ed
		// eliminazione si aprono solo da controlli gia' nascosti dai permessi,
		// ma quel giorno che si aggiunge una scorciatoia da tastiera o un
		// pulsante nuovo, il gate deve essere gia' qui.

		onDragEnter(e) {
			// Senza questa guardia la drop zone si illumina anche a chi non puo'
			// caricare, promettendo un'operazione che verra' poi scartata.
			if (!this.canCreate) return
			return DmsMixin.methods.onDragEnter.call(this, e)
		},

		handleDrop(e) {
			if (!this.canCreate) {
				this.isDragging = false
				this.dragCount = 0
				this.showSnackbar('Non hai i permessi per caricare documenti', 'warning')
				return
			}
			return DmsMixin.methods.handleDrop.call(this, e)
		},

		createFolder() {
			if (!this.canCreate) return
			return DmsMixin.methods.createFolder.call(this)
		},

		renameItem() {
			if (!this.canEdit) return
			return DmsMixin.methods.renameItem.call(this)
		},

		moveItem() {
			if (!this.canEdit) return
			return DmsMixin.methods.moveItem.call(this)
		},

		saveTags() {
			if (!this.canEdit) return
			return DmsMixin.methods.saveTags.call(this)
		},

		confirmBulkMove() {
			if (!this.canEdit) return
			return DmsMixin.methods.confirmBulkMove.call(this)
		},

		deleteItem() {
			if (!this.canDelete) return
			return DmsMixin.methods.deleteItem.call(this)
		},

		confirmBulkDelete() {
			if (!this.canDelete) return
			return DmsMixin.methods.confirmBulkDelete.call(this)
		},

		uploadFiles(files) {
			if (!this.canCreate) return
			return DmsMixin.methods.uploadFiles.call(this, files)
		},

		uploadFilesWithPaths(files) {
			if (!this.canCreate) return
			return DmsMixin.methods.uploadFilesWithPaths.call(this, files)
		},

		handleVersionUpload(e) {
			if (!this.canCreate) return
			return DmsMixin.methods.handleVersionUpload.call(this, e)
		},

		onItemDragStart(e, type, item) {
			if (!this.canEdit) {
				e.preventDefault()
				return
			}
			return DmsMixin.methods.onItemDragStart.call(this, e, type, item)
		},

		onFolderDrop(e, folder) {
			if (!this.canEdit) {
				this.dragItem = null
				this.dragOverFolder = null
				return
			}
			return DmsMixin.methods.onFolderDrop.call(this, e, folder)
		},

		// --- ACL per cartella ---

		/**
		 * Input:  nessuno (usa contextMenu.item)
		 * Fa:     apre il dialog dei permessi caricando configurazione propria,
		 *         eventuale ereditarieta' e l'elenco dei ruoli del tenant
		 * Output: nessuno
		 */
		async openFolderRolesDialog() {
			var folder = this.contextMenu.item
			this.contextMenu.show = false
			if (!folder) return

			this.folderRolesTarget = folder
			this.folderRoleIds = []
			this.folderRolesInherited = false
			this.folderRolesInheritedFrom = null
			this.folderRolesLoading = true
			this.folderRolesDialog = true

			try {
				var resp = await this.axios.get(this.apiBase + '/folders/' + folder.id + '/roles')
				if (resp.data.success) {
					this.availableRoles = resp.data.data.roles || []
					this.folderRoleIds = resp.data.data.selected || []
					this.folderRolesInherited = !!resp.data.data.inherited
					this.folderRolesInheritedFrom = resp.data.data.inherited_from || null
				}
			} catch (e) {
				this.showSnackbar(this.getErrorMessage(e, 'Impossibile leggere i permessi della cartella'), 'error')
				this.folderRolesDialog = false
			} finally {
				this.folderRolesLoading = false
			}
		},

		/**
		 * Input:  nessuno (usa folderRolesTarget e folderRoleIds)
		 * Fa:     sostituisce la configurazione della cartella. Lista vuota =
		 *         rimuove la restrizione, non la chiude a tutti.
		 * Output: nessuno (ricarica il contenuto per aggiornare i lucchetti)
		 */
		async saveFolderRoles() {
			// L'azione piu' delicata del modulo: riscrive chi puo' leggere una
			// cartella. La guardia qui non e' ridondante rispetto al v-if sulla
			// voce di menu, e' l'ultima difesa lato client.
			if (!this.canEdit) return
			if (!this.folderRolesTarget) return

			this.saving = true
			try {
				var resp = await this.axios.put(
					this.apiBase + '/folders/' + this.folderRolesTarget.id + '/roles',
					{ role_ids: this.folderRoleIds }
				)
				this.folderRolesDialog = false
				this.showSnackbar(resp.data.message || 'Permessi aggiornati', 'success')
				// onAfterMutation, non solo loadContent: ricarica anche il
				// pannello Attivita' recenti, come fanno tutte le altre mutazioni
				this.onAfterMutation()
			} catch (e) {
				this.showSnackbar(this.getErrorMessage(e, 'Errore nel salvataggio dei permessi'), 'error')
			} finally {
				this.saving = false
			}
		},

		// --- Condivisione con le aziende ---
		//
		// Arriva dai File Condivisi, che era una sezione separata sotto Aziende.
		// Da non confondere con i permessi cartella qui sopra: quelli dicono
		// quali RUOLI interni vedono una cartella, questa pubblica un singolo
		// documento sul portale di un'AZIENDA.

		/**
		 * Input:  il documento
		 * Fa:     come il mixin, ma carica anche l'elenco delle condivisioni da
		 *         mostrare nel pannello di dettaglio
		 * Output: nessuno
		 */
		openDocDetails(doc) {
			DmsMixin.methods.openDocDetails.call(this, doc)
			if (this.canShare) this.loadShares(doc.id)
		},

		async loadShares(docId) {
			try {
				var resp = await this.axios.get(this.apiBase + '/documents/' + docId + '/shares')
				this.docShares = resp.data.success ? (resp.data.data || []) : []
			} catch (e) {
				this.docShares = []
			}
		},

		openShareDialog() {
			var item = this.contextMenu.item || this.selectedDoc
			if (!item || !this.canShare) return
			this.contextMenu.item = item
			this.contextMenu.type = 'doc'
			this.shareCompanyIds = []
			this.shareDialog = true
		},

		async shareDocument() {
			var doc = this.contextMenu.item || this.selectedDoc
			if (!doc || !this.shareCompanyIds.length) return

			this.saving = true
			try {
				await this.axios.post(this.apiBase + '/documents/' + doc.id + '/share', {
					company_ids: this.companyIdsOf(this.shareCompanyIds)
				})
				this.shareDialog = false
				this.showSnackbar('Documento condiviso', 'success')
				this.loadContent()
				if (this.selectedDoc && this.selectedDoc.id === doc.id) this.loadShares(doc.id)
			} catch (e) {
				this.showSnackbar(this.getErrorMessage(e, 'Errore nella condivisione'), 'error')
			} finally {
				this.saving = false
			}
		},

		async removeShare(share) {
			try {
				await this.axios.post(this.apiBase + '/documents/' + this.selectedDoc.id + '/unshare', {
					company_ids: [share.company_id]
				})
				this.showSnackbar('Condivisione rimossa', 'success')
				this.loadShares(this.selectedDoc.id)
				this.loadContent()
			} catch (e) {
				this.showSnackbar(this.getErrorMessage(e, 'Errore nella rimozione'), 'error')
			}
		},

		openBulkShareDialog() {
			if (!this.canShare) return
			this.bulkShareCompanyIds = []
			this.bulkShareDialog = true
		},

		async confirmBulkShare() {
			if (!this.selectedDocIds.length || !this.bulkShareCompanyIds.length) return

			this.bulkLoading = true
			try {
				await this.axios.post(this.apiBase + '/bulk-share', {
					attachment_ids: this.selectedDocIds,
					company_ids: this.companyIdsOf(this.bulkShareCompanyIds)
				})
				this.bulkShareDialog = false
				this.clearSelection()
				this.loadContent()
				this.showSnackbar('Documenti condivisi', 'success')
			} catch (e) {
				this.showSnackbar(this.getErrorMessage(e, 'Errore nella condivisione'), 'error')
			} finally {
				this.bulkLoading = false
			}
		},

		/**
		 * AsyncAutocomplete ha return-object attivo, quindi restituisce
		 * {value, text}: al backend servono i soli id.
		 */
		companyIdsOf(selection) {
			return selection.map(c => typeof c === 'object' ? c.value : c)
		}
	}
}
</script>

<style src="@/css/dms.css"></style>

<style scoped>
.marquee-rect {
	position: absolute;
	border: 1px dashed var(--v-primary-base, #1976d2);
	background: rgba(25, 118, 210, 0.08);
	pointer-events: none;
	z-index: 5;
}
.dms-doc-card.selected-multi,
.v-card.selected-multi {
	outline: 2px solid var(--v-primary-base, #1976d2);
	outline-offset: -2px;
}
.bulk-toolbar {
	position: sticky;
	top: 0;
	z-index: 4;
}
/* Badge condivisioni: angolo opposto ai tag, che stanno in basso a sinistra. */
.dms-doc-shared {
	position: absolute;
	top: 4px;
	right: 4px;
}
</style>
